import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import { api } from "../../lib/api";
import { Download, Coins, TrendingUp, Wine, ShoppingBag } from "lucide-react";

const COLORS = ["#7A2A36", "#D4A93C", "#4F7942", "#A6534F", "#6B4226", "#8B7355"];

export default function AdminFinancialReports() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);

    useEffect(() => {
        setData(null);
        api.get(`/admin/reports/financial?days=${days}`).then((r) => setData(r.data));
    }, [days]);

    function exportCSV() {
        if (!data) return;
        const lines = [
            ["Métrica", "Valor (EUR)"],
            ["Receita bruta", data.summary.revenue],
            ["Subtotal produtos", data.summary.subtotal],
            ["Descontos", data.summary.discount],
            ["Envio", data.summary.shipping],
            ["Comissão plataforma (15%)", data.summary.commission],
            ["A pagar aos produtores", data.summary.net_producers],
            ["Receita assinaturas", data.summary.subs_revenue],
            ["Nº encomendas", data.summary.orders_count],
            ["Ticket médio", data.summary.avg_order_value],
            [""],
            ["Por categoria", ""],
            ...data.by_category.map((c) => [c.name, c.value]),
            [""],
            ["Por região", ""],
            ...data.by_region.map((c) => [c.name, c.value]),
        ];
        const csv = lines.map((row) => row.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `relatorio_financeiro_${days}d.csv`;
        a.click();
        URL.revokeObjectURL(url);
    }

    return (
        <section className="space-y-6" data-testid="admin-reports">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <span className="label-overline text-wine/60">Financeiro</span>
                    <h1 className="font-heading text-3xl text-wine-dark">Relatórios</h1>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="flex gap-1 bg-cream rounded-md p-1">
                        {[7, 30, 90, 365].map((d) => (
                            <button key={d} onClick={() => setDays(d)} className={`px-3 py-1.5 rounded-md text-xs uppercase tracking-wider ${days === d ? "bg-wine text-cream" : "text-wine-dark/60"}`} data-testid={`period-${d}`}>
                                {d}d
                            </button>
                        ))}
                    </div>
                    <button onClick={exportCSV} disabled={!data} className="btn-wine-outline text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="export-csv">
                        <Download className="h-3.5 w-3.5" /> CSV
                    </button>
                </div>
            </div>

            {!data ? <p className="text-wine-dark/60 label-overline">A carregar…</p> : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi label="Receita bruta" value={`€${data.summary.revenue.toFixed(2)}`} icon={TrendingUp} accent />
                        <Kpi label="Comissão (15%)" value={`€${data.summary.commission.toFixed(2)}`} icon={Coins} />
                        <Kpi label="A pagar produtores" value={`€${data.summary.net_producers.toFixed(2)}`} icon={Coins} />
                        <Kpi label="Receita Clube" value={`€${data.summary.subs_revenue.toFixed(2)}`} icon={Wine} />
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <Kpi label="Encomendas" value={data.summary.orders_count} icon={ShoppingBag} small />
                        <Kpi label="Ticket médio" value={`€${data.summary.avg_order_value.toFixed(2)}`} icon={ShoppingBag} small />
                        <Kpi label="Descontos" value={`€${data.summary.discount.toFixed(2)}`} icon={Coins} small />
                        <Kpi label="Envio" value={`€${data.summary.shipping.toFixed(2)}`} icon={Coins} small />
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card title="Receita por categoria">
                            {data.by_category.length === 0 ? <Empty /> : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={data.by_category} layout="vertical" margin={{ left: 60 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#7A2A36" strokeOpacity={0.08} horizontal={false} />
                                            <XAxis type="number" stroke="#7A2A36" strokeOpacity={0.4} fontSize={11} tickFormatter={(v) => `€${v}`} />
                                            <YAxis type="category" dataKey="name" stroke="#7A2A36" strokeOpacity={0.7} fontSize={11} width={100} />
                                            <Tooltip contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }} formatter={(v) => [`€${v}`, "Receita"]} />
                                            <Bar dataKey="value" fill="#7A2A36" radius={[0, 4, 4, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Card>

                        <Card title="Receita por região">
                            {data.by_region.length === 0 ? <Empty /> : (
                                <div className="h-64">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie data={data.by_region} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                                                {data.by_region.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                            </Pie>
                                            <Tooltip contentStyle={{ background: "#FBF8F3", border: "1px solid #D4A93C", borderRadius: 6, fontSize: 12 }} formatter={(v, n) => [`€${v}`, n]} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </Card>
                    </div>

                    <Card title={`Top 10 produtores (últimos ${days} dias)`}>
                        {data.by_producer.length === 0 ? <Empty /> : (
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left label-overline text-wine-dark/60">
                                        <th className="py-2">#</th><th>Produtor</th><th className="text-right">Receita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.by_producer.map((p, i) => (
                                        <tr key={p.name} className="border-t border-wine-dark/5">
                                            <td className="py-2 text-wine-dark/40 font-heading">{String(i + 1).padStart(2, "0")}</td>
                                            <td className="text-wine-dark">{p.name}</td>
                                            <td className="text-right font-heading text-base text-wine-dark">€{p.value.toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </Card>
                </>
            )}
        </section>
    );
}

function Kpi({ label, value, icon: Icon, accent, small }) {
    return (
        <div className={`p-${small ? 4 : 5} rounded-md border shadow-sm ${accent ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-start justify-between">
                <span className={`label-overline ${accent ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${accent ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className={`font-heading ${small ? "text-2xl" : "text-3xl"} mt-2`}>{value}</p>
        </div>
    );
}

function Card({ title, children }) {
    return (
        <div className="bg-white rounded-md border border-gold-soft/30 p-5 shadow-sm">
            <h2 className="font-heading text-xl text-wine-dark mb-4">{title}</h2>
            {children}
        </div>
    );
}

function Empty() { return <p className="text-wine-dark/55 text-sm">Sem dados no período.</p>; }
